import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Input } from "./Input";
import { Button, ButtonVariant, ButtonSize } from "@/shared/ui/Button";
import { Option } from "./SearchableSelect";
import { MultiSelectChips } from "./MultiSelectChips";

interface ServerSideSearchableSelectProps {
  id?: string;
  options: Option[];
  value: Option[];
  /**
   * Raw selected IDs (string[]) — passed by wrapper components alongside `value`.
   * When provided, the component will auto-seed selectedCache from `options` as they
   * load (via pagination) whenever a loaded option's ID appears in `selectedIds` but
   * is not yet in the cache. This solves the problem of pre-selected IDs from URL
   * params that are not in the first paginated page.
   */
  selectedIds?: string[];
  /**
   * Pre-fetched Option objects for items that cannot be found via normal pagination
   * (e.g., billing codes fetched by specific IDs from URL params). These are merged
   * into selectedCache exactly once when they first arrive asynchronously.
   */
  initialValue?: Option[];
  onChange: (value: Option[]) => void;
  placeholder?: string;
  "data-testid"?: string;
  onLoadMore: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  /**
   * True when a new search query is in-flight (queryKey changed, fresh fetch).
   * Distinct from isFetchingNextPage which only covers pagination.
   * When true, previous results are kept visible instead of clearing to empty.
   */
  isSearching?: boolean;
  onSearchChange: (search: string) => void;
  multiple?: boolean;
  dropdownDirection?: "up" | "down";
  showClearButton?: boolean;
  showNoneOption?: boolean;
  noneOptionLabel?: string;
  /**
   * When true, renders the dropdown via a React Portal at document.body so it
   * escapes any overflow:hidden / overflow-y-auto ancestor (e.g. modals).
   * Position is computed from the trigger element's bounding rect.
   */
  usePortal?: boolean;
  /**
   * Minimum height (px) for the portal dropdown.
   * Defaults to 60. Raise this when the dropdown appears inside a modal
   * where available space may be constrained.
   */
  minDropdownHeight?: number;
  /**
   * Maximum number of chips to display before showing "+n more" badge.
   * Defaults to 3 if not specified.
   */
  maxVisibleChips?: number;
  /**
   * Optional className applied to the selected value text span in single-select mode.
   * Use e.g. "text-primary" to colour the selected value blue.
   */
  selectedValueClassName?: string | undefined;
  /**
   * Number of static/fixed options always prepended to `options` by the parent
   * (e.g. "None" / "Any" in SponsorSelect). These are excluded from the
   * "No results" count so the message still shows when only static options remain.
   * Defaults to 0.
   */
  staticOptionsCount?: number;
  /**
   * Optional function that maps a selected Option to a URL string.
   * When provided, each selected chip in multi-select mode is rendered as an
   * anchor (<a>) tag so the user can right-click → "Open in new tab", matching
   * the Legacy DRT application behaviour for Provider and Run filter options.
   */
  getItemUrl?: (item: Option) => string | undefined;
  /**
   * Maximum number of items that can be selected in multi-select mode.
   * When the limit is reached, unselected options are disabled and a message
   * is shown inside the dropdown. Has no effect in single-select mode.
   */
  maxSelections?: number;
  /**
   * When true, the dropdown trigger button is focused automatically on mount.
   * Useful when the component is the primary interaction point on a page load.
   */
  focusOnMount?: boolean;
}

export const ServerSideSearchableSelect: React.FC<ServerSideSearchableSelectProps> = ({
  id,
  options,
  value,
  selectedIds,
  initialValue,
  onChange,
  placeholder,
  "data-testid": dataTestId,
  onLoadMore,
  hasNextPage,
  isFetchingNextPage,
  isSearching = false,
  onSearchChange,
  multiple = true,
  dropdownDirection = "down",
  showClearButton = true,
  showNoneOption = false,
  noneOptionLabel,
  usePortal = false,
  minDropdownHeight = 60,
  maxVisibleChips = 3,
  selectedValueClassName,
  staticOptionsCount = 0,
  getItemUrl,
  maxSelections,
  focusOnMount = false,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  // isSearchPending: true from when user types until options arrive for that search term.
  // Prevents the flash of "No results" during the debounce + API fetch gap.
  const [isSearchPending, setIsSearchPending] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout>();

  // Portal dropdown position (fixed coords computed from trigger bounding rect)
  const [portalStyle, setPortalStyle] = useState<React.CSSProperties>({});

  // Find the visible bottom limit for portal dropdowns (clamped by modal or viewport)
  const getVisibleBottom = (el: HTMLElement): number => {
    let bottom = window.innerHeight;
    let parent = el.parentElement;
    while (parent && parent !== document.body) {
      const s = window.getComputedStyle(parent);
      if (/auto|scroll/.test(s.overflowY) || /auto|scroll/.test(s.overflow)) {
        bottom = Math.min(bottom, parent.getBoundingClientRect().bottom);
        break;
      }
      parent = parent.parentElement;
    }
    return bottom;
  };

  const updatePortalPosition = useCallback(() => {
    if (!usePortal || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const MARGIN = 8;
    // When rendering via a portal at document.body, use the full viewport —
    // not the clamped modal/scroll-container bounds from getVisibleBottom.
    const spaceBelow = window.innerHeight - rect.bottom - MARGIN;
    const spaceAbove = rect.top - MARGIN;

    if (dropdownDirection === "up") {
      setPortalStyle({
        position: "fixed",
        left: rect.left,
        bottom: window.innerHeight - rect.top + 4,
        width: rect.width,
        zIndex: 9999,
        maxHeight: Math.max(minDropdownHeight, Math.min(320, spaceAbove)),
      });
    } else {
      if (spaceBelow < minDropdownHeight && spaceAbove > spaceBelow) {
        // flip upward
        setPortalStyle({
          position: "fixed",
          left: rect.left,
          bottom: window.innerHeight - rect.top + 4,
          width: rect.width,
          zIndex: 9999,
          maxHeight: Math.max(minDropdownHeight, Math.min(320, spaceAbove)),
        });
      } else {
        setPortalStyle({
          position: "fixed",
          left: rect.left,
          top: rect.bottom + 4,
          width: rect.width,
          zIndex: 9999,
          maxHeight: Math.max(minDropdownHeight, spaceBelow),
        });
      }
    }
  }, [usePortal, dropdownDirection, minDropdownHeight]); // eslint-disable-line react-hooks/exhaustive-deps

  // When the portal dropdown is open, recompute its position on any scroll or
  // resize so it tracks the trigger element even if the modal/page scrolls.
  // Also close the dropdown if the trigger scrolls out of the visible viewport.
  useEffect(() => {
    if (!isOpen || !usePortal) return;
    const handleRepositionEvent = () => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > window.innerHeight) {
          setIsOpen(false);
          return;
        }
      }
      updatePortalPosition();
    };
    // Capture phase so we catch scroll events on any ancestor (modal, page, etc.)
    window.addEventListener("scroll", handleRepositionEvent, true);
    window.addEventListener("resize", handleRepositionEvent);
    return () => {
      window.removeEventListener("scroll", handleRepositionEvent, true);
      window.removeEventListener("resize", handleRepositionEvent);
    };
  }, [isOpen, usePortal, updatePortalPosition]); // eslint-disable-line react-hooks/exhaustive-deps

  // Accumulate all loaded options across pages so toggling the dropdown
  // doesn't lose previously loaded items. Reset only when search changes.
  const [allLoadedOptions, setAllLoadedOptions] = useState<Option[]>([]);
  // Track the active search term separately from the debounced React state,
  // so the accumulation effect always has the correct current value.
  const activeSearchRef = useRef<string>("");
  // Track the search term that produced the current allLoadedOptions,
  // so we know when to reset vs. accumulate.
  const lastAccumulatedSearchRef = useRef<string>("");

  useEffect(() => {
    const currentSearch = activeSearchRef.current;
    if (currentSearch !== lastAccumulatedSearchRef.current) {
      // Search changed — only replace the accumulated list once the new results
      // have actually arrived (i.e. the query is no longer loading/fetching).
      // While isSearching or isFetchingNextPage is true the options array may
      // still be stale/empty; keep the previous results visible so the user
      // sees the stale list with "Searching..." instead of a blank dropdown.
      if (!isSearching && !isFetchingNextPage) {
        lastAccumulatedSearchRef.current = currentSearch;
        setAllLoadedOptions(options);
        // Results have arrived — clear the pending flag
        setIsSearchPending(false);
      }
      // If still fetching, leave allLoadedOptions and isSearchPending as-is.
    } else {
      // Same search (or no search) — merge new options into accumulated list
      setAllLoadedOptions((prev) => {
        const existingIds = new Set(prev.map((o) => String(o.id)));
        const newItems = options.filter((o) => !existingIds.has(String(o.id)));
        return newItems.length > 0 ? [...prev, ...newItems] : prev;
      });
      // Options have arrived for the current search — clear the pending flag
      setIsSearchPending(false);
    }
  }, [options, isFetchingNextPage, isSearching]); // eslint-disable-line react-hooks/exhaustive-deps

  // Internal cache of selected Option objects — the SOLE source of truth for what is selected.
  const [selectedCache, setSelectedCache] = useState<Option[]>(value);

  // Track IDs that have been seeded into selectedCache via selectedIds or initialValue.
  const seededIdsRef = useRef<Set<string>>(new Set(value.map((v) => String(v.id))));

  // Track which IDs have been applied via initialValue (allows re-application for new IDs).
  const appliedInitialIdsRef = useRef<Set<string>>(new Set(value.map((v) => String(v.id))));

  // ── Keep selectedCache in sync with selectedIds ────────────────────────────
  // When selectedIds changes (e.g. saved filter switched), remove any cached
  // items that are no longer in the selection.  This prevents stale chips from
  // a previous filter appearing alongside the new selection.
  // NOTE: Only runs when selectedIds is explicitly provided (not undefined).
  // When selectedIds is undefined (e.g. usePublicId mode in RunSelect), the parent
  // manages selections via the `value` prop — we must not wipe selectedCache here.
  useEffect(() => {
    if (selectedIds === undefined) return;
    const currentIds = new Set(selectedIds);
    setSelectedCache((prev) => {
      const filtered = prev.filter((item) => currentIds.has(String(item.id)));
      // Also clear seededIdsRef for removed items so they can be re-seeded later
      prev.forEach((item) => {
        if (!currentIds.has(String(item.id))) {
          seededIdsRef.current.delete(String(item.id));
        }
      });
      return filtered.length !== prev.length ? filtered : prev;
    });
  }, [selectedIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-seed from paginated options ──────────────────────────────────────
  // When options load (paginated) and contain an ID listed in selectedIds that
  // is not yet in the cache, seed it in.  This handles pre-selected IDs from
  // URL params or saved filters that arrive on a later page of results.
  useEffect(() => {
    if (!selectedIds || selectedIds.length === 0) return;
    const selectedIdSet = new Set(selectedIds);
    const newToSeed = options.filter((opt) => {
      const idStr = String(opt.id);
      return selectedIdSet.has(idStr) && !seededIdsRef.current.has(idStr);
    });
    if (newToSeed.length > 0) {
      newToSeed.forEach((opt) => seededIdsRef.current.add(String(opt.id)));
      setSelectedCache((prev) => {
        const existingIds = new Set(prev.map((p) => String(p.id)));
        const truly_new = newToSeed.filter((o) => !existingIds.has(String(o.id)));
        return truly_new.length > 0 ? [...prev, ...truly_new] : prev;
      });
    }
  }, [options, selectedIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Seed from initialValue (pre-fetched by parent for off-page IDs) ────────
  // Fires whenever initialValue gains new Option objects.  Tracks applied IDs
  // so already-seeded items are not duplicated, but new IDs (e.g. after a
  // saved-filter switch) are always applied.
  useEffect(() => {
    if (!initialValue || initialValue.length === 0) return;
    const newItems = initialValue.filter(
      (opt) => !appliedInitialIdsRef.current.has(String(opt.id))
    );
    if (newItems.length === 0) return;
    newItems.forEach((opt) => {
      appliedInitialIdsRef.current.add(String(opt.id));
      seededIdsRef.current.add(String(opt.id));
    });
    setSelectedCache((prev) => {
      const existingIds = new Set(prev.map((p) => String(p.id)));
      const toAdd = newItems.filter((o) => !existingIds.has(String(o.id)));
      return toAdd.length > 0 ? [...prev, ...toAdd] : prev;
    });
  }, [initialValue]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Hard reset detection ───────────────────────────────────────────────────
  // When both value and selectedIds go to [] the parent explicitly cleared the
  // selection (e.g. Reset button).  Wipe the cache and all tracking refs so the
  // component starts fresh.
  const prevValueRef = useRef(value);
  useEffect(() => {
    const prevValue = prevValueRef.current;
    prevValueRef.current = value;

    const valueWentEmpty = value.length === 0 && prevValue.length > 0;
    const selectedIdsEmpty = !selectedIds || selectedIds.length === 0;

    if (valueWentEmpty && selectedIdsEmpty) {
      setSelectedCache([]);
      seededIdsRef.current = new Set();
      appliedInitialIdsRef.current = new Set();
    }
  }, [value, selectedIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-focus the search input whenever the dropdown opens
  useEffect(() => {
    if (!isOpen) return;
    // Use a short timeout to allow the portal/DOM to render first
    const timer = setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);
    return () => clearTimeout(timer);
  }, [isOpen]);

  // Focus the trigger button on mount when focusOnMount is true
  useEffect(() => {
    if (!focusOnMount) return;
    const timer = setTimeout(() => {
      triggerRef.current?.focus();
    }, 0);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const portalDropdownRef = useRef<HTMLDivElement>(null);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setSearchTerm("");
    setIsSearchPending(false);
    activeSearchRef.current = "";
    onSearchChange("");
  }, [onSearchChange]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const inTrigger = dropdownRef.current?.contains(target);
      const inPortalDropdown = portalDropdownRef.current?.contains(target);

      // Check if click is on any scrollbar (page scrollbar or dropdown scrollbar)
      if (target instanceof Element) {
        const rect = target.getBoundingClientRect();
        // Detect scrollbar click: click X is beyond element's client width, or Y beyond client height
        const isOnScrollbar =
          event.clientX > rect.left + target.clientWidth ||
          event.clientY > rect.top + target.clientHeight;

        if (isOnScrollbar) {
          return; // Don't close if clicking any scrollbar
        }
      }

      if (!inTrigger && !inPortalDropdown) {
        closeDropdown();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, closeDropdown]);

  // ── Centralised keyboard handler (WCAG 2.1 §4.1.2 / APG combobox pattern) ───────────────
  // Single document-level capture-phase listener active only while the dropdown is open.
  //
  //  ESC        → close dropdown + return focus to trigger
  //  ArrowDown  → focus first option in the list (from trigger)
  //  ArrowUp    → focus last option in the list (from trigger)
  useEffect(() => {
    if (!isOpen) return;

    const getOptions = () =>
      listRef.current
        ? Array.from(
            listRef.current.querySelectorAll<HTMLElement>('[role="button"],[tabindex="0"]')
          )
        : [];

    const handleKeyDown = (event: KeyboardEvent) => {
      const focused = document.activeElement as HTMLElement | null;

      switch (event.key) {
        case "Escape": {
          event.preventDefault();
          event.stopImmediatePropagation();
          // Capture the trigger ref before closing (portal unmount may shift focus)
          const trigger = triggerRef.current;
          closeDropdown();
          // Defer focus to after React re-renders and unmounts the portal dropdown,
          // otherwise the portal unmount can steal focus back to document.body.
          requestAnimationFrame(() => {
            trigger?.focus();
          });
          break;
        }
        case "ArrowDown": {
          event.preventDefault();
          const opts = getOptions();
          if (opts.length === 0) break;
          if (focused === triggerRef.current) {
            opts[0].focus();
            break;
          }
          const idx = opts.indexOf(focused as HTMLElement);
          opts[(idx + 1 + opts.length) % opts.length].focus();
          break;
        }
        case "ArrowUp": {
          event.preventDefault();
          const opts = getOptions();
          if (opts.length === 0) break;
          if (focused === triggerRef.current) {
            opts[opts.length - 1].focus();
            break;
          }
          const idx = opts.indexOf(focused as HTMLElement);
          if (idx === -1) {
            opts[opts.length - 1].focus();
            break;
          }
          opts[(idx - 1 + opts.length) % opts.length].focus();
          break;
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [isOpen, closeDropdown]);

  const handleToggle = useCallback(() => {
    if (isOpen) {
      closeDropdown();
    } else {
      updatePortalPosition();
      setIsOpen(true);
    }
  }, [isOpen, closeDropdown, updatePortalPosition]);

  const handleScroll = useCallback(() => {
    if (listRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = listRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 5 && hasNextPage && !isFetchingNextPage) {
        onLoadMore();
      }
    }
  }, [hasNextPage, isFetchingNextPage, onLoadMore]);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newSearch = e.target.value;
      setSearchTerm(newSearch);
      // Update the active search ref immediately so the options effect can detect the change
      activeSearchRef.current = newSearch;
      // Mark search as pending — prevents "No results" flash during debounce + API fetch gap
      setIsSearchPending(true);
      // Debounce the actual API call
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      searchTimeoutRef.current = setTimeout(() => {
        onSearchChange(newSearch);
      }, 300);
    },
    [onSearchChange]
  );

  const handleSelect = useCallback(
    (option: Option) => {
      if (multiple) {
        const optionId = String(option.id);
        const isSelected = selectedCache.some((item) => String(item.id) === optionId);
        // Enforce maxSelections: block adding a new item when the limit is already reached
        if (!isSelected && maxSelections !== undefined && selectedCache.length >= maxSelections) {
          return;
        }
        const newSelected = isSelected
          ? selectedCache.filter((item) => String(item.id) !== optionId)
          : [...selectedCache, option];
        // Update cache immediately — do NOT wait for value prop round-trip
        setSelectedCache(newSelected);
        onChange(newSelected);
      } else {
        // Single select mode
        setSelectedCache([option]);
        onChange([option]);
        closeDropdown();
      }
    },
    [selectedCache, onChange, multiple, closeDropdown, maxSelections]
  );

  const handleClear = useCallback(() => {
    setSelectedCache([]);
    onChange([]);
    closeDropdown();
  }, [onChange, closeDropdown]);

  const handleSelectNone = useCallback(() => {
    setSelectedCache([]);
    onChange([]);
    closeDropdown();
  }, [onChange, closeDropdown]);

  // Split display options: selected on top (highlighted), then divider, then unselected.
  // Use selectedCache so that selections persist even when options change due to search.
  const { selectedInList, unselectedInList } = useMemo(() => {
    const selectedIds = new Set(selectedCache.map((v) => String(v.id)));
    const unselectedInList = allLoadedOptions.filter((o) => !selectedIds.has(String(o.id)));
    return { selectedInList: selectedCache, unselectedInList };
  }, [allLoadedOptions, selectedCache]);

  // True when the max-selections cap is reached in multi-select mode
  const isAtMaxSelections =
    multiple && maxSelections !== undefined && selectedCache.length >= maxSelections;

  const renderOption = (option: Option, isSelected: boolean) => {
    const isDisabled = !isSelected && isAtMaxSelections;
    // When the parent provided a URL builder (linkItems pattern used by
    // Provider / Driver / Vehicle filters), wrap the option in an <a>
    // pointing at the detail page. This lets Cmd/Ctrl-click and middle-click
    // open the entity in a new tab — matching legacy DRT behaviour — while
    // plain left-click still toggles the multi-select checkbox.
    const optionUrl = getItemUrl ? getItemUrl(option) : undefined;

    /*
     * Visual discoverability for the linkItems / getItemUrl pattern,
     * tuned per UX feedback to match user mental models:
     *
     *  • SINGLE-SELECT dropdowns (multiple === false) — plain click
     *  commits the selection AND the row IS a link (the underlying
     *    <a> already supports Cmd/Ctrl+click → new tab). One affordance,
     *    two behaviours, so blue text + hover-underline is the right
     *    visual hint. Matches what a hyperlink looks like everywhere
     *    else in the app.
     *
     *  • MULTI-SELECT dropdowns (multiple === true) — the primary
     *    affordance is the checkbox: plain click toggles selection.
     *    Showing options in blue conflicts with that intent (it would
     *    suggest "navigate"). So multi-select options stay in default
     *    text color; the link affordance lives on the SELECTED CHIPS
     *    above the trigger instead, where Cmd/Ctrl+click semantically
     *    makes sense ("open this thing I've already chosen"). Chip-level
     *    linking is wired below by passing getItemUrl into
     *    <MultiSelectChips>; chips already render in text-primary and
     *    use <a href> when getItemUrl is supplied — so visually they
     *    are blue links.
     *
     * Non-linkable options (e.g. Sponsor's T_NO_SPONSOR / T_ANY_SPONSOR
     * sentinels — getItemUrl returns undefined for those) always stay
     * in default text-text.
     */
    const isLinkable = !!optionUrl;
    const baseClass = `px-4 py-2 flex items-start text-[0.85rem] leading-5 w-full text-left no-underline ${
      isDisabled
        ? "opacity-40 cursor-not-allowed"
        : isSelected
          ? "text-primary hover:bg-surface-alt cursor-pointer"
          : isLinkable
            ? "text-tableBlueColor hover:text-text hover:bg-surface-alt cursor-pointer"
            : "text-text hover:bg-surface-alt cursor-pointer"
    }`;

    const handleClick = (e: React.MouseEvent) => {
      // Cmd (mac) / Ctrl (win) / Shift / middle-click → fall through so the
      // browser opens the link in a new tab/window. Plain left-click toggles
      // the selection without navigating.
      if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) {
        if (!optionUrl) e.preventDefault();
        return;
      }
      e.preventDefault();
      if (!isDisabled) handleSelect(option);
    };

    const innerContent = (
      <>
        {multiple && (
          <input
            type="checkbox"
            readOnly
            checked={isSelected}
            disabled={isDisabled}
            className="w-4 h-4 flex-shrink-0 mr-2 mt-0.5 rounded border-border"
            tabIndex={-1}
          />
        )}
        {option.sublabel ? (
          <span className="flex flex-col min-w-0">
            <span className="break-words">{option.name}</span>
            <span className="text-text-muted text-[0.78rem] leading-4 break-words">
              {option.sublabel}
            </span>
          </span>
        ) : (
          <span className="break-words">{option.name}</span>
        )}
      </>
    );

    return (
      <li key={option.id}>
        {optionUrl ? (
          <a
            href={optionUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleClick}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                if (!isDisabled) handleSelect(option);
              }
            }}
            aria-disabled={isDisabled}
            data-testid={`${dataTestId}-option-${option.id}`}
            className={baseClass}
            tabIndex={isDisabled ? -1 : 0}
          >
            {innerContent}
          </a>
        ) : (
          <div
            role="button"
            tabIndex={isDisabled ? -1 : 0}
            aria-disabled={isDisabled}
            onClick={(e) => {
              // Cmd/Ctrl/Shift/middle-click should not toggle selection —
              // ignore modifier clicks to prevent accidental deselection.
              if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) return;
              if (!isDisabled) handleSelect(option);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                if (!isDisabled) handleSelect(option);
              }
            }}
            data-testid={`${dataTestId}-option-${option.id}`}
            className={baseClass}
          >
            {innerContent}
          </div>
        )}
      </li>
    );
  };

  const selectedNamesLabel =
    selectedCache.length > 0
      ? selectedCache.map((v) => v.name).join(", ")
      : (placeholder ?? undefined);
  const selectedCount = selectedIds?.length ?? selectedCache.length;
  const showSelectedNames =
    multiple && selectedCount > 0 && selectedCount <= 3 && selectedCache.length === selectedCount;
  const selectedSummaryLabel = showSelectedNames
    ? selectedCache.map((v) => v.name).join(", ")
    : `${selectedCount} selected`;

  // Shared dropdown content (used for both portal and inline rendering)
  const dropdownContent = (
    <div
      ref={usePortal ? portalDropdownRef : undefined}
      className={`bg-surface border border-border-strong rounded-md shadow-lg flex flex-col ${
        usePortal
          ? ""
          : `absolute z-50 min-w-full w-full ${
              dropdownDirection === "up" ? "bottom-full mb-1" : "top-full mt-1"
            }`
      }`}
      style={usePortal ? { ...portalStyle, overflow: "hidden" } : undefined}
    >
      <div className="p-2">
        <div className="relative">
          <Input
            ref={searchInputRef}
            type="text"
            placeholder={t("filters.searchPlaceholder")}
            value={searchTerm}
            onChange={handleSearchChange}
            className="pl-8"
            data-testid={`${dataTestId}-search-input`}
          />
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-text-muted" size={16} />
        </div>
      </div>
      {isAtMaxSelections && (
        <p
          className="px-3 py-1.5 text-xs text-text-muted border-b border-border"
          data-testid={`${dataTestId}-max-reached-message`}
        >
          {t("common.maximumSelectionsReached", "Maximum {{count}} selections reached", {
            count: maxSelections,
          })}
        </p>
      )}
      {showClearButton && (
        <Button
          variant={ButtonVariant.Ghost}
          size={ButtonSize.Sm}
          onClick={handleClear}
          className="w-full text-left px-3 py-2 text-sm text-primary hover:bg-surface-alt justify-start border-t border-b border-border rounded-none"
          data-testid={`${dataTestId}-clear-button`}
        >
          {t("filters.clear")}
        </Button>
      )}
      <ul ref={listRef} onScroll={handleScroll} className="max-h-60 overflow-y-auto">
        {showNoneOption && !multiple && (
          <li
            className={`px-4 py-2 cursor-pointer flex items-center text-[0.85rem] leading-5 ${
              selectedCache.length === 0
                ? "text-primary hover:bg-surface-alt"
                : "hover:bg-surface-alt"
            }`}
            onClick={handleSelectNone}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleSelectNone();
              }
            }}
            role="button"
            tabIndex={0}
            data-testid={`${dataTestId}-option-none`}
          >
            {noneOptionLabel || t("filters.none", "None")}
          </li>
        )}
        {selectedInList.length > 0 &&
          selectedInList.map((option) => renderOption(option, true))}
        {selectedInList.length > 0 && unselectedInList.length > 0 && (
          <li className="border-t border-border my-1" role="separator" />
        )}
        {unselectedInList.map((option) => renderOption(option, false))}
        {(isSearching || isFetchingNextPage || isSearchPending) && (
          <li className="text-center p-2 text-text-muted">
            {t("common.searching", "Searching...")}
          </li>
        )}
        {!isSearching &&
          !isFetchingNextPage &&
          !isSearchPending &&
          allLoadedOptions.length <= staticOptionsCount && (
            <li className="text-center p-2 text-text-muted">{t("common.noResults")}</li>
          )}
      </ul>
    </div>
  );

  return (
    <div id={id} className="relative" ref={dropdownRef} data-testid={dataTestId}>
      <div
        ref={triggerRef}
        role="button"
        tabIndex={0}
        className="w-full min-h-[36px] h-auto border border-border-strong rounded-[4px] text-left flex justify-between items-center gap-2 py-[6px] pl-[10px] pr-[32px] text-[14px] text-foreground bg-surface-secondary hover:border-primary focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary focus:border-transparent overflow-hidden"
        onClick={handleToggle}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleToggle();
          }
        }}
        data-testid={`${dataTestId}-button`}
        title={selectedCache.length > 0 ? selectedNamesLabel : undefined}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <div className="flex-1 min-w-0 pr-2">
          {selectedCache.length > 0 ? (
            multiple ? (
              /*
               * Chips already render in text-primary; forwarding
               * getItemUrl turns each chip into <a href> so
               * Cmd/Ctrl/middle-click opens the entity in a new tab
               * (legacy DRT parity). This is where the link
               * affordance lives in multi-select mode — the
               * dropdown options themselves stay in default text
               * color (see baseClass above) so the checkbox stays
               * the unambiguous primary affordance there.
               */
              <MultiSelectChips
                value={selectedCache}
                onChange={(nextValue) => {
                  setSelectedCache(nextValue);
                  onChange(nextValue);
                }}
                options={allLoadedOptions}
                placeholder={placeholder ?? t("filters.searchPlaceholder")}
                maxVisible={maxVisibleChips}
                {...(getItemUrl ? { getItemUrl } : {})}
              />
            ) : (
              <span
                className={`truncate block${selectedValueClassName ? ` ${selectedValueClassName}` : getItemUrl ? ' text-tableBlueColor' : ""}`}
              >
                {selectedCache[0].name}
              </span>
            )
          ) : (
            <span className="truncate block text-accordionArrow">
              {placeholder ?? t("filters.searchPlaceholder")}
            </span>
          )}
        </div>
        <ChevronDown
          size={16}
          className={`transform transition-transform flex-shrink-0 absolute right-[10px] ${
            isOpen ? "rotate-180 text-text" : "text-accordionArrow"
          }`}
        />
      </div>

      {isOpen && (usePortal ? createPortal(dropdownContent, document.body) : dropdownContent)}
    </div>
  );
};
